import {useCallback, useEffect, useRef, useState} from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  FilePlus2,
  History,
  MessageSquareText,
  Paperclip,
  Save,
  SlidersHorizontal,
  Tags,
  Upload,
} from 'lucide-react'
import {formatTopicRequestAuthor, formatTopicRequestLabel} from '../data/requestIdentity'
import type {BriefingTopic} from '../data/topics'
import {validateTopicForm, type TopicFormDefinition} from '../data/topicForms'
import {
  listTopicRequests,
  saveTopicRequest,
  MAX_TOPIC_ATTACHMENTS,
  MAX_TOPIC_FILE_SIZE,
  MAX_TOPIC_TOTAL_FILE_SIZE,
  TopicApiError,
  type TopicPromotion,
  type TopicReference,
  type TopicRequest,
} from '../services/topicsApi'
import TopicField from './TopicField'
import {TopicFormSection, TopicLayout} from './TopicLayout'
import TopicPromotionPicker from './TopicPromotionPicker'
import TopicReferencePicker from './TopicReferencePicker'
import TopicRequestHistory from './TopicRequestHistory'
import TopicAttachmentList from './TopicAttachmentList'

export type TopicRequestDraft = {
  values: Record<string, unknown>
  promotions: TopicPromotion[]
  originalList: TopicPromotion[]
  families: TopicReference[]
  segmentation: TopicReference[]
  details: string
  attachments: File[]
}

type Props = {
  topic: BriefingTopic
  definition: TopicFormDefinition
  draft?: TopicRequestDraft
  onDraftChange?: (draft: TopicRequestDraft) => void
  onSavingChange?: (saving: boolean) => void
}

export default function TopicRequestForm({
  topic,
  definition,
  draft,
  onDraftChange,
  onSavingChange,
}: Props) {
  const [tab, setTab] = useState<'form' | 'history'>('form')
  const [values, setValues] = useState<Record<string, unknown>>(draft?.values || {})
  const [promotions, setPromotions] = useState<TopicPromotion[]>(draft?.promotions || [])
  const [originalList, setOriginalList] = useState<TopicPromotion[]>(draft?.originalList || [])
  const [families, setFamilies] = useState<TopicReference[]>(draft?.families || [])
  const [segmentation, setSegmentation] = useState<TopicReference[]>(draft?.segmentation || [])
  const [details, setDetails] = useState(draft?.details || '')
  const [attachments, setAttachments] = useState<File[]>(draft?.attachments || [])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [notice, setNotice] = useState<{type: 'success' | 'error'; message: string} | null>(null)
  const [saving, setSaving] = useState(false)
  const [requests, setRequests] = useState<TopicRequest[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState('')
  const submitting = useRef(false)
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    onDraftChange?.({
      values,
      promotions,
      originalList,
      families,
      segmentation,
      details,
      attachments,
    })
  }, [
    values,
    promotions,
    originalList,
    families,
    segmentation,
    details,
    attachments,
    onDraftChange,
  ])

  useEffect(() => {
    onSavingChange?.(saving)
  }, [saving, onSavingChange])

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    setHistoryError('')
    try {
      setRequests(await listTopicRequests(topic.id))
    } catch (error) {
      setHistoryError((error as Error).message)
    } finally {
      setHistoryLoading(false)
    }
  }, [topic.id])

  useEffect(() => {
    if (tab === 'history') loadHistory()
  }, [tab, loadHistory])

  const clearError = (key: string) => {
    setErrors((current) => {
      const next = {...current}
      delete next[key]
      return next
    })
    setNotice(null)
  }

  const changeValue = (key: string, value: string | string[]) => {
    setValues((current) => ({...current, [key]: value}))
    clearError(key)
  }

  const addAttachments = (files: FileList | null) => {
    if (!files) return
    const next = [...attachments]
    Array.from(files).forEach((file) => {
      if (
        !next.some(
          (existing) =>
            existing.name === file.name &&
            existing.size === file.size &&
            existing.lastModified === file.lastModified
        )
      )
        next.push(file)
    })
    let message = ''
    if (next.length > MAX_TOPIC_ATTACHMENTS) message = 'Adicione no máximo 10 anexos.'
    else if (next.some((file) => file.size > MAX_TOPIC_FILE_SIZE))
      message = 'Cada anexo deve ter no máximo 10 MB.'
    else if (next.reduce((total, file) => total + file.size, 0) > MAX_TOPIC_TOTAL_FILE_SIZE)
      message = 'Os anexos devem somar no máximo 25 MB.'
    if (message) {
      setErrors((current) => ({...current, attachments: message}))
      return
    }
    setAttachments(next)
    clearError('attachments')
  }

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting.current) return
    const input = {
      promotionIds: promotions.map((promotion) => promotion.id),
      values,
      details,
      attachmentCount: attachments.length,
    }
    const fieldErrors = validateTopicForm(definition, input)
    if (
      definition.disallowSuggestedList &&
      promotions.some((promotion) => promotion.mechanicId === 4)
    ) {
      fieldErrors.promotionIds = 'Este tópico não é válido para promoções de lista sugerida.'
    }
    setErrors(fieldErrors)
    setNotice(null)
    if (Object.keys(fieldErrors).length) {
      setNotice({
        type: 'error',
        message: 'Revise os campos obrigatórios ou inválidos antes de salvar.',
      })
      formRef.current?.scrollIntoView?.({behavior: 'smooth', block: 'start'})
      return
    }
    submitting.current = true
    setSaving(true)
    try {
      const saved = await saveTopicRequest(
        topic.id,
        {
          promotionIds: input.promotionIds,
          values: input.values,
          details: input.details,
        },
        attachments
      )
      setRequests((current) => [saved, ...current.filter((request) => request.id !== saved.id)])
      setValues({})
      setPromotions([])
      setOriginalList([])
      setFamilies([])
      setSegmentation([])
      setDetails('')
      setAttachments([])
      setErrors({})
      setNotice({
        type: 'success',
        message:
          formatTopicRequestLabel(saved) +
          ' salva com sucesso, realizada por ' +
          formatTopicRequestAuthor(saved) +
          '.',
      })
    } catch (error) {
      if (error instanceof TopicApiError) setErrors(error.fields)
      setNotice({type: 'error', message: (error as Error).message})
    } finally {
      submitting.current = false
      setSaving(false)
    }
  }

  const beforePromotions = topic.id === 'alteracao-segmento' ? definition.fields : []
  const afterPromotions = topic.id === 'alteracao-segmento' ? [] : definition.fields
  const renderField = (field: TopicFormDefinition['fields'][number]) => (
    <TopicField
      key={field.key}
      field={field}
      value={values[field.key]}
      onChange={changeValue}
      error={errors[field.key]}
      disabled={saving}
    />
  )

  const renderFields = (fields: TopicFormDefinition['fields']) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))'},
        gap: 2.5,
      }}
    >
      {fields.map((field) => {
        const wide =
          fields.length === 1 ||
          field.type === 'textarea' ||
          field.type === 'checkbox' ||
          !!field.help ||
          (field.type === 'radio' &&
            (field.label.length > 38 || field.options?.some((option) => option.label.length > 30)))
        return (
          <Box key={field.key} sx={{minWidth: 0, gridColumn: {md: wide ? '1 / -1' : 'auto'}}}>
            {renderField(field)}
          </Box>
        )
      })}
    </Box>
  )

  return (
    <TopicLayout>
      <Box
        sx={{
          minWidth: 0,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2.5,
          boxShadow: '0 2px 8px rgba(15,23,42,0.03)',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_event, value) => setTab(value)}
          variant='standard'
          scrollButtons={false}
          aria-label='Navegação das solicitações'
          sx={{
            px: {xs: 1, sm: 2},
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              minWidth: 0,
              maxWidth: 'none',
              flex: {xs: 1, sm: 'none'},
              px: {xs: 1, sm: 2},
              fontSize: {xs: '13px', sm: '14px'},
            },
            '& .MuiTab-icon': {display: {xs: 'none', sm: 'inline-flex'}},
          }}
        >
          <Tab
            icon={<FilePlus2 size={17} aria-hidden='true' />}
            iconPosition='start'
            label='Nova solicitação'
            value='form'
            id='topic-form-tab'
            aria-controls='topic-form-panel'
            disabled={saving}
          />
          <Tab
            icon={<History size={17} aria-hidden='true' />}
            iconPosition='start'
            label='Solicitações salvas'
            value='history'
            id='topic-history-tab'
            aria-controls='topic-history-panel'
            disabled={saving}
          />
        </Tabs>
        <Box
          role='tabpanel'
          id={'topic-' + tab + '-panel'}
          aria-labelledby={'topic-' + tab + '-tab'}
          sx={{minWidth: 0}}
        >
          {notice && (
            <Alert severity={notice.type} sx={{mx: {xs: 2, sm: 3}, mt: 2.5}}>
              {notice.message}
            </Alert>
          )}
          {tab === 'history' ? (
            <Box sx={{p: {xs: 2, sm: 3}}}>
              <TopicRequestHistory
                topicId={topic.id}
                definition={definition}
                requests={requests}
                loading={historyLoading}
                error={historyError}
                onRefresh={loadHistory}
              />
            </Box>
          ) : (
            <Box component='form' ref={formRef} noValidate onSubmit={submit} sx={{minWidth: 0}}>
              <Box sx={{px: {xs: 2, sm: 3}, pt: 3, pb: 2.5}}>
                <Typography component='h2' variant='h6'>
                  Nova solicitação
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{mt: 0.5}}>
                  Preencha os campos marcados com *.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  px: {xs: 2, sm: 3},
                  pb: 3,
                  minWidth: 0,
                }}
              >
                {definition.disallowSuggestedList && (
                  <Alert severity='warning'>Não válido para a mecânica de lista sugerida.</Alert>
                )}
                {beforePromotions.length > 0 && (
                  <TopicFormSection
                    title='Alteração desejada'
                    description='Escolha o tipo de alteração na segmentação.'
                    icon={SlidersHorizontal}
                  >
                    {renderFields(beforePromotions)}
                  </TopicFormSection>
                )}
                {definition.requiresPromotions && (
                  <TopicFormSection
                    title='Promoções'
                    description='Busque pelo nome ou ID e selecione as promoções da solicitação.'
                    icon={Tags}
                  >
                    <TopicPromotionPicker
                      topicId={topic.id}
                      label='Selecione uma ou mais promoções para alteração'
                      value={promotions}
                      onChange={(selected) => {
                        setPromotions(selected)
                        clearError('promotionIds')
                      }}
                      error={errors.promotionIds}
                      disallowSuggestedList={definition.disallowSuggestedList}
                      disabled={saving}
                    />
                  </TopicFormSection>
                )}
                {(afterPromotions.length > 0 ||
                  definition.requiresOriginalList ||
                  definition.hasSegmentationSelector ||
                  definition.requiresFamilies) && (
                  <TopicFormSection
                    title='Dados da solicitação'
                    description='Informe as opções e os dados deste tópico.'
                    icon={SlidersHorizontal}
                  >
                    {renderFields(afterPromotions)}
                    {definition.requiresOriginalList && (
                      <Box sx={{mt: 2.5}}>
                        <TopicPromotionPicker
                          topicId={topic.id}
                          label='Lista Original para a segmentação da Lista de Recompra'
                          value={originalList}
                          onChange={(selected) => {
                            setOriginalList(selected)
                            changeValue('listaOriginalId', selected[0]?.id || '')
                          }}
                          error={errors.listaOriginalId}
                          listOnly
                          multiple={false}
                          disabled={saving}
                        />
                      </Box>
                    )}
                    {definition.hasSegmentationSelector && (
                      <Box sx={{mt: 2.5}}>
                        <TopicReferencePicker
                          topicId={topic.id}
                          kind='segmentacoes'
                          label='Selecione um card de segmentação para alteração'
                          value={segmentation}
                          onChange={(selected) => {
                            setSegmentation(selected)
                            changeValue('segmentacaoId', selected[0]?.id || '')
                          }}
                          required={false}
                          multiple={false}
                          error={errors.segmentacaoId}
                          disabled={saving}
                        />
                      </Box>
                    )}
                    {definition.requiresFamilies && (
                      <Box sx={{mt: 2.5}}>
                        <TopicReferencePicker
                          topicId={topic.id}
                          kind='familias'
                          label='Selecione um card de família para alteração'
                          value={families}
                          onChange={(selected) => {
                            setFamilies(selected)
                            changeValue(
                              'familiaIds',
                              selected.map((family) => family.id)
                            )
                          }}
                          error={errors.familiaIds}
                          disabled={saving}
                        />
                      </Box>
                    )}
                  </TopicFormSection>
                )}
                <TopicFormSection
                  title='Informações complementares'
                  description='Acrescente detalhes e arquivos, se necessário.'
                  icon={MessageSquareText}
                >
                  <TextField
                    id='topic-request-details'
                    label='Detalhamento da solicitação'
                    multiline
                    minRows={4}
                    fullWidth
                    size='small'
                    value={details}
                    disabled={saving}
                    error={!!errors.details}
                    helperText={errors.details}
                    placeholder='Descreva sua solicitação...'
                    inputProps={{maxLength: 20000}}
                    onChange={(event) => {
                      setDetails(event.target.value)
                      clearError('details')
                    }}
                  />
                  <Box
                    sx={{
                      mt: 3,
                      border: '1px dashed',
                      borderColor: '#cbd5e1',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: '#f8fafc',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: {xs: 'column', sm: 'row'},
                        alignItems: {sm: 'center'},
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0}}>
                        <Box sx={{color: 'text.secondary', mt: 0.25}}>
                          <Paperclip size={20} aria-hidden='true' />
                        </Box>
                        <Box sx={{minWidth: 0}}>
                          <Typography variant='subtitle2' fontWeight={600}>
                            Anexos (se houver)
                          </Typography>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{display: 'block', mt: 0.5}}
                          >
                            Até 10 arquivos de 10 MB cada, somando no máximo 25 MB. Visualize os
                            anexos antes de salvar.
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<Upload size={16} />}
                        component='label'
                        disabled={saving}
                        sx={{
                          flexShrink: 0,
                          bgcolor: 'white',
                          alignSelf: {xs: 'stretch', sm: 'center'},
                        }}
                      >
                        Adicionar arquivos
                        <input
                          type='file'
                          multiple
                          hidden
                          aria-label='Adicionar anexos'
                          disabled={saving}
                          onChange={(event) => {
                            addAttachments(event.target.files)
                            event.target.value = ''
                          }}
                        />
                      </Button>
                    </Box>
                    {errors.attachments && (
                      <Alert severity='error' sx={{mt: 2}}>
                        {errors.attachments}
                      </Alert>
                    )}
                    {attachments.length > 0 && (
                      <Box sx={{mt: 2}}>
                        <TopicAttachmentList
                          files={attachments}
                          removeDisabled={saving}
                          onRemove={(file) => {
                            setAttachments((current) => current.filter((item) => item !== file))
                            clearError('attachments')
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </TopicFormSection>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: {xs: 'column', sm: 'row'},
                  alignItems: {sm: 'center'},
                  justifyContent: 'space-between',
                  gap: 2,
                  p: {xs: 2, sm: 3},
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: '#f8fafc',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Confira as informações antes de salvar.
                </Typography>
                <Button
                  type='submit'
                  variant='contained'
                  disabled={saving}
                  startIcon={
                    saving ? <CircularProgress size={18} color='inherit' /> : <Save size={18} />
                  }
                  sx={{minWidth: 150, width: {xs: '100%', sm: 'auto'}}}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </TopicLayout>
  )
}
